var Emitter = require('../emitter')
var Timer = require('../timer')
var Log = require('../log')
var Util = require('../util')
var Chrono = require('../chrono')
var version = require('../version')
var Constants = require('../constants')

var YBRequest = require('../comm/request')
var Communication = require('../comm/communication')
var FlowTransform = require('../comm/transform/flow')
var ViewTransform = require('../comm/transform/view')
var Nqs6Transform = require('../comm/transform/nqs6')
var ResourceTransform = require('../comm/transform/resource')

var Options = require('./options')
var RequestBuilder = require('./requestbuilder')

var Adapter = require('../adapter/adapter')

var Plugin = Emitter.extend(
  /** @lends Plugin.prototype */
  {
    /**
     * This is the main class of video analytics. You may want one instance for each video you want
     * to track. Will need adapters for both content and ads, manage options and general flow.
     *
     * @constructs Plugin
     * @extends Emitter
     * @memberof youbora
     *
     * @param {Options} [options] An object complying with {@link Options} constructor.
     * @param {Adapter} [adapter] If an adapter is provided, setAdapter will be immediately called.
     */
    constructor: function (options, adapter) {
      /** This flags indicates that /init has been called. */
      this.isInitiated = false

      /** This flags indicate that the content is preloading. */
      this.isPreloading = false

      /** Chrono for preload times. */
      this.preloadChrono = new Chrono()

      /** Chrono for init to join time. */
      this.initChrono = new Chrono()

      this.options = new Options(options)
      this._adapter = null
      this._adsAdapter = null
      this._ping = this._ping = new Timer(this._sendPing.bind(this), 5000)

      this.requestBuilder = new RequestBuilder(this)

      this.resourceTransform = new ResourceTransform(this)

      // FastData
      this.viewTransform = new ViewTransform(this)
      this.viewTransform.on(ViewTransform.Event.DONE, function (e) {
        this._ping.interval = e.target.response.pingTime * 1000
      }.bind(this))

      if (adapter) this.setAdapter(adapter)

      this.viewTransform.init()
    },

    /**
     * Reset all variables and stop all timers
     */
    _reset: function () {
      this._stopPings()
      this.resourceTransform = new ResourceTransform(this)

      this.isInitiated = false
      this.isPreloading = false
      this.initChrono.reset()
      this.preloadChrono.reset()
    },

    // ----------------------------------------- Options ------------------------------------------

    /**
     * Modifies current options. See {@link Options.setOptions}.
     *
     * @param {any} options
     */
    setOptions: function (options) {
      this.options.setOptions(options)
    },

    // ----------------------------------------- Adapters -----------------------------------------

    /**
     * Sets an adapter for video content.
     *
     * @param {Adapter} adapter
     */
    setAdapter: function (adapter) {
      if (adapter.plugin) {
        Log.warn('Adapters can only be added to a single plugin')
      } else {
        this.removeAdapter()

        this._adapter = adapter
        adapter.plugin = this

        // Register listeners
        adapter.on(Adapter.Event.START, this._startListener.bind(this))
        adapter.on(Adapter.Event.JOIN, this._joinListener.bind(this))
        adapter.on(Adapter.Event.PAUSE, this._pauseListener.bind(this))
        adapter.on(Adapter.Event.RESUME, this._resumeListener.bind(this))
        adapter.on(Adapter.Event.SEEK_BEGIN, this._seekBeginListener.bind(this))
        adapter.on(Adapter.Event.SEEK_END, this._seekEndListener.bind(this))
        adapter.on(Adapter.Event.BUFFER_BEGIN, this._bufferBeginListener.bind(this))
        adapter.on(Adapter.Event.BUFFER_END, this._bufferEndListener.bind(this))
        adapter.on(Adapter.Event.ERROR, this._errorListener.bind(this))
        adapter.on(Adapter.Event.STOP, this._stopListener.bind(this))
      }
    },

    /**
     * Returns current adapter or null.
     *
     * @returns {Adapter}
     */
    getAdapter: function () {
      return this._adapter
    },

    /** Removes the current adapter. Fires stop if needed. Calls adapter.dispose() */
    removeAdapter: function () {
      if (this._adapter) {
        this._adapter.dispose()

        this._adapter.plugin = null

        this._adapter.off(Adapter.Event.START, this._startListener)
        this._adapter.off(Adapter.Event.JOIN, this._joinListener)
        this._adapter.off(Adapter.Event.PAUSE, this._pauseListener)
        this._adapter.off(Adapter.Event.RESUME, this._resumeListener)
        this._adapter.off(Adapter.Event.SEEK_BEGIN, this._seekBeginListener)
        this._adapter.off(Adapter.Event.SEEK_END, this._seekEndListener)
        this._adapter.off(Adapter.Event.BUFFER_BEGIN, this._bufferBeginListener)
        this._adapter.off(Adapter.Event.BUFFER_END, this._bufferEndListener)
        this._adapter.off(Adapter.Event.ERROR, this._errorListener)
        this._adapter.off(Adapter.Event.STOP, this._stopListener)

        this._adapter = null
      }
    },

    /**
     * Sets an adapter for ads.
     *
     * @param {Adapter} adsAdapter
     */
    setAdsAdapter: function (adsAdapter) {
      if (adsAdapter.plugin) {
        Log.warn('Adapters can only be added to a single plugin')
      } else {
        this.removeAdsAdapter()

        adsAdapter.plugin = this
        this._adsAdapter = adsAdapter

        adsAdapter.on(Adapter.Event.START, this._adStartListener.bind(this))
        adsAdapter.on(Adapter.Event.JOIN, this._adJoinListener.bind(this))
        adsAdapter.on(Adapter.Event.PAUSE, this._adPauseListener.bind(this))
        adsAdapter.on(Adapter.Event.RESUME, this._adResumeListener.bind(this))
        adsAdapter.on(Adapter.Event.BUFFER_BEGIN, this._adBufferBeginListener.bind(this))
        adsAdapter.on(Adapter.Event.BUFFER_END, this._adBufferEndListener.bind(this))
        adsAdapter.on(Adapter.Event.STOP, this._adStopListener.bind(this))
      }
    },

    /**
     * Returns current adapter or null.
     *
     * @returns {Adapter}
     */
    getAdsAdapter: function () {
      return this._adsAdapter
    },

    /** Removes the current adapter. Fires stop if needed. Calls adapter.dispose() */
    removeAdsAdapter: function () {
      if (this._adsAdapter) {
        this._adsAdapter.dispose()

        this._adsAdapter.plugin = null

        this._adsAdapter.off(Adapter.Event.START, this._adStartListener)
        this._adsAdapter.off(Adapter.Event.JOIN, this._adJoinListener)
        this._adsAdapter.off(Adapter.Event.PAUSE, this._adPauseListener)
        this._adsAdapter.off(Adapter.Event.RESUME, this._adResumeListener)
        this._adsAdapter.off(Adapter.Event.BUFFER_BEGIN, this._adBufferBeginListener)
        this._adsAdapter.off(Adapter.Event.BUFFER_END, this._adBufferEndListener)
        this._adsAdapter.off(Adapter.Event.STOP, this._adStopListener)

        this._adsAdapter = null
      }
    },

    // ----------------------------------------- DISABLE ------------------------------------------

    /**
     * Disable request sending.
     */
    disable: function () {
      this.setOptions({ 'enabled': false })
    },

    /**
     * Re-enable request sending.
     */
    enable: function () {
      this.setOptions({ 'enabled': true })
    },

    // ---------------------------------------- LISTENERS -----------------------------------------

    /**
     * Creates and enqueues related request using {@link Communication#sendRequest}.
     * It will fire will-send-events.
     *
     * @param {string} willSendEvent Name of the will-send event. Use {@link Plugin.Event} enum.
     * @param {string} service Name of the service. Use {@link Constants.Service} enum.
     * @param {Object} params Params of the request
     */
    _send: function (willSendEvent, service, params) {
      params = this.requestBuilder.buildParams(params, service)

      var data = {
        params: params,
        plugin: this,
        adapter: this.getAdapter(),
        adsAdapter: this.getAdsAdapter()
      }

      this.emit(willSendEvent, data)

      if (this._comm && params !== null && this.options.enabled) {
        this.lastServeiceSent = service
        this._comm.sendRequest(new YBRequest(null, service, params))
      }
    },

    /**
     * Initializes comm and its transforms.
     */
    _initComm: function () {
      this.resourceTransform.init(this.getResource())

      this._comm = new Communication()
      this._comm.addTransform(new FlowTransform())
      this._comm.addTransform(this.viewTransform)
      this._comm.addTransform(this.resourceTransform)
      this._comm.addTransform(new Nqs6Transform())
    },

    /**
     * Starts preloading state and chronos.
     */
    firePreloadBegin: function () {
      if (!this.isPreloading) {
        this.isPreloading = true
        this.preloadChrono.start()
      }
    },

    /**
     * Ends preloading state and chronos.
     */
    firePreloadEnd: function () {
      if (this.isPreloading) {
        this.isPreloading = false
        this.preloadChrono.stop()
      }
    },

    /**
     * Sends /init. Should be called once the user has requested the content. Does not need
     * a working adapter or player to work. it won't sent start if isInitiated is true.
     *
     * @param {Object} [params] Object of key:value params.
     */
    fireInit: function (params) {
      if (!this.isInitiated) {
        this.viewTransform.nextView()
        this._initComm()
        this._startPings()

        this.isInitiated = true
        this.initChrono.start()

        params = params || {}
        this._send(Plugin.Event.WILL_SEND_INIT, Constants.Service.INIT, params)
        Log.notice(Constants.Service.INIT + ' ' + (params.title || params.mediaResource))
      }
    },

    /**
     * Sends /error. Should be used when the error is related to out-of-player errors: like async
     * resource load or player loading errors.
     *
     * @param {String|Object} [code] Error Code, if an object is sent, it will be treated as params.
     * @param {String} [msg] Error Message
     * @param {Object} [metadata] Object defining error metadata
     * @param {String} [level] Level of the error. Currently supports 'error' and 'fatal'
     */
    fireError: function (object, msg, metadata, level) {
      if (!this._comm) this._initComm()

      var params = Util.buildErrorParams(object, msg, metadata, level)

      this._send(Plugin.Event.WILL_SEND_ERROR, Constants.Service.ERROR, params)
      Log.notice(Constants.Service.ERROR +
        ' ' + params.errorLevel +
        ' ' + params.errorCode
      )

      if (params.errorLevel === 'fatal') this._reset()
    },

    /**
     * Fires /stop. Should be used to terminate sessions once the player is gone or if
     * plugin.fireError() is called.
     *
     * @param {Object} [params] Object of key:value params.
     */
    fireStop: function (params) {
      params = params || {}
      this._send(Plugin.Event.WILL_SEND_STOP, Constants.Service.STOP, params)

      Log.notice(Constants.Service.STOP + ' at ' + params.playhead + 's')

      this._reset()
    },

    _startListener: function (e) {
      if (!this.isInitiated || this.lastServeiceSent === Constants.Service.ERROR) {
        this.viewTransform.nextView()
        this._initComm()
        this._startPings()
      }

      var params = e.data.params || {}
      this._send(Plugin.Event.WILL_SEND_START, Constants.Service.START, params)
      Log.notice(Constants.Service.START + ' ' + (params.title || params.mediaResource))
    },

    _joinListener: function (e) {
      var params = e.data.params || {}
      if (!this._adsAdapter || !this._adsAdapter.flags.isStarted) {
        this._send(Plugin.Event.WILL_SEND_JOIN, Constants.Service.JOIN, params)
        Log.notice(Constants.Service.JOIN + ' ' + params.joinDuration + 'ms')
      } else { // If it is currently showing ads, join is invalidated
        if (this._adapter.monitor) this._adapter.monitor.stop()
        this._adapter.flags.isJoined = false
        this._adapter.chronos.join.stopTime = 0
      }
    },

    _pauseListener: function (e) {
      if (this._adapter) {
        if (this._adapter.flags.isBuffering ||
          this._adapter.flags.isSeeking ||
          (this._adsAdapter && this._adsAdapter.flags.isStarted)) {
          this._adapter.chronos.pause.reset()
        }
      }

      var params = e.data.params || {}
      this._send(Plugin.Event.WILL_SEND_PAUSE, Constants.Service.PAUSE, params)
      Log.notice(Constants.Service.PAUSE + ' at ' + params.playhead + 's')
    },

    _resumeListener: function (e) {
      var params = e.data.params || {}
      this._send(Plugin.Event.WILL_SEND_RESUME, Constants.Service.RESUME, params)
      Log.notice(Constants.Service.RESUME + ' ' + params.pauseDuration + 'ms')
    },

    _seekBeginListener: function (e) {
      if (this._adapter && this._adapter.flags.isPaused) this._adapter.chronos.pause.reset()
      Log.notice('Seek Begin')
    },

    _seekEndListener: function (e) {
      var params = e.data.params || {}
      this._send(Plugin.Event.WILL_SEND_SEEK, Constants.Service.SEEK, params)
      Log.notice(Constants.Service.SEEK +
        ' to ' + params.playhead +
        ' in ' + params.seekDuration + 'ms'
      )
    },

    _bufferBeginListener: function (e) {
      if (this._adapter && this._adapter.flags.isPaused) this._adapter.chronos.pause.reset()
      Log.notice('Buffer Begin')
    },

    _bufferEndListener: function (e) {
      var params = e.data.params || {}
      this._send(Plugin.Event.WILL_SEND_BUFFER, Constants.Service.BUFFER, params)
      Log.notice(Constants.Service.BUFFER +
        ' to ' + params.playhead +
        ' in ' + params.bufferDuration + 'ms'
      )
    },

    _errorListener: function (e) {
      this.fireError(e.data.params || {})
    },

    _stopListener: function (e) {
      this.fireStop(e.data.params || {})
    },

    // Ads
    _adStartListener: function (e) {
      if (this._adapter) {
        this._adapter.fireBufferEnd()
        this._adapter.fireSeekEnd()
        if (this._adapter.flags.isPaused) this._adapter.chronos.pause.reset()
      }

      var params = e.data.params || {}
      params.adNumber = this.requestBuilder.getNewAdNumber()
      this._send(Plugin.Event.WILL_SEND_AD_START, Constants.Service.AD_START, params)
      Log.notice(
        Constants.Service.AD_START + ' ' +
        params.adPosition + params.adNumber +
        ' at ' + params.playhead + 's'
      )
    },

    _adJoinListener: function (e) {
      var params = e.data.params || {}
      params.adNumber = this.requestBuilder.lastSent.adNumber
      this._send(Plugin.Event.WILL_SEND_AD_JOIN, Constants.Service.AD_JOIN, params)
      Log.notice(Constants.Service.AD_JOIN + ' ' + params.adJoinDuration + 'ms')
    },

    _adPauseListener: function (e) {
      var params = e.data.params || {}
      params.adNumber = this.requestBuilder.lastSent.adNumber
      this._send(Plugin.Event.WILL_SEND_AD_PAUSE, Constants.Service.AD_PAUSE, params)
      Log.notice(Constants.Service.AD_PAUSE + ' at ' + params.adPlayhead + 's')
    },

    _adResumeListener: function (e) {
      var params = e.data.params || {}
      params.adNumber = this.requestBuilder.lastSent.adNumber
      this._send(Plugin.Event.WILL_SEND_AD_RESUME, Constants.Service.AD_RESUME, params)
      Log.notice(Constants.Service.AD_RESUME + ' ' + params.adPauseDuration + 'ms')
    },

    _adBufferBeginListener: function (e) {
      Log.notice('Ad Buffer Begin')
      if (this._adsAdapter && this._adsAdapter.flags.isPaused) {
        this._adsAdapter.chronos.pause.reset()
      }
    },

    _adBufferEndListener: function (e) {
      var params = e.data.params || {}
      params.adNumber = this.requestBuilder.lastSent.adNumber
      this._send(Plugin.Event.WILL_SEND_AD_BUFFER, Constants.Service.AD_BUFFER, params)
      Log.notice(Constants.Service.AD_BUFFER + ' ' + params.adBufferDuration + 'ms')
    },

    _adStopListener: function (e) {
      // remove time from joinDuration
      if (this._adapter && !this._adapter.flags.isJoined) {
        this._adapter.chronos.join.startTime = Math.min(
          this._adapter.chronos.join.startTime + this._adsAdapter.chronos.total.getDeltaTime(),
          new Date().getTime()
        )
      }

      var params = e.data.params || {}
      params.adNumber = this.requestBuilder.lastSent.adNumber
      this._send(Plugin.Event.WILL_SEND_AD_STOP, Constants.Service.AD_STOP, params)
      Log.notice(Constants.Service.AD_STOP + ' ' + params.adTotalDuration + 'ms')
    },

    // ------------------------------------------ PINGS -------------------------------------------

    /**
     * Starts sending pings
     * @private
     */
    _startPings: function () {
      if (!this._ping.isRunning) this._ping.start()
    },

    /**
     * Stops sending pings
     * @private
     */
    _stopPings: function () {
      this._ping.stop()
    },

    _sendPing: function (diffTime) {
      var params = {
        diffTime: diffTime,
        entities: this.requestBuilder.getChangedEntities()
      }
      if (this._adapter) {
        if (this._adapter.flags.isPaused) {
          params = this.requestBuilder.fetchParams(params, ['pauseDuration'])
        } else {
          params = this.requestBuilder.fetchParams(params, ['bitrate', 'throughput', 'fps'])

          if (this._adsAdapter && this._adsAdapter.flags.isStarted) {
            params = this.requestBuilder.fetchParams(params, ['adBitrate'])
          }
        }
        if (this._adapter.flags.isJoined) {
          params = this.requestBuilder.fetchParams(params, ['playhead'])
        }
        if (this._adapter.flags.isBuffering) {
          params = this.requestBuilder.fetchParams(params, ['bufferDuration'])
        }
        if (this._adapter.flags.isSeeking) {
          params = this.requestBuilder.fetchParams(params, ['seekDuration'])
        }

        if (this._adsAdapter) {
          if (this._adsAdapter.flags.isStarted) {
            params = this.requestBuilder.fetchParams(params, ['adPlayhead'])
          }
          if (this._adsAdapter.flags.isBuffering) {
            params = this.requestBuilder.fetchParams(params, ['adBufferDuration'])
          }
        }
      }

      this._send(Plugin.Event.WILL_SEND_PING, Constants.Service.PING, params)
      Log.verbose(Constants.Service.PING)
    },

    // --------------------------------------- INFO GETTERS ---------------------------------------

    /** Returns service host */
    getHost: function () {
      return Util.addProtocol(
        Util.stripProtocol(this.options['host']),
        this.options['httpSecure'])
    },

    /** Returns parse HLS Flag */
    isParseHls: function () {
      return this.options['parse.Hls']
    },

    /** Returns parse CdnNode Flag */
    isParseCdnNode: function () {
      return this.options['parse.CdnNode']
    },

    /** Returns Cdn list */
    getParseCdnNodeList: function () {
      return this.options['parse.CdnNode.list']
    },

    /** Returns Cdn header name */
    getParseCdnNodeNameHeader: function () {
      return this.options['parse.CdnNameHeader']
    },

    /** Returns content's playhead */
    getPlayhead: function () {
      var ret = 0
      if (this._adapter) {
        try {
          ret = this._adapter.getPlayhead()
        } catch (err) {
          Log.warn('An error occurred while calling getPlayhead', err)
        }
      }
      return Util.parseNumber(ret, 0)
    },

    /** Returns content's PlayRate */
    getPlayrate: function () {
      var ret = 0
      if (this._adapter) {
        try {
          ret = this._adapter.getPlayrate()
        } catch (err) {
          Log.warn('An error occured while calling getPlayrate', err)
        }
      }
      return ret
    },

    /** Returns content's FramesPerSecond */
    getFramesPerSecond: function () {
      var ret = null
      if (this._adapter) {
        try {
          ret = this._adapter.getFramesPerSecond()
        } catch (err) {
          Log.warn('An error occured while calling getFramesPerSecond', err)
        }
      }
      return ret
    },

    /** Returns content's DroppedFrames */
    getDroppedFrames: function () {
      var ret = null
      if (this._adapter) {
        try {
          ret = this._adapter.getDroppedFrames()
        } catch (err) {
          Log.warn('An error occured while calling getDroppedFrames', err)
        }
      }
      if (!ret) {
        ret = this.getWebkitDroppedFrames()
      }
      return Util.parseNumber(ret, 0)
    },

    /**
     * Returns dropped frames as per webkitDroppedFrameCount
     *
     * @returns {number}
     */
    getWebkitDroppedFrames: function () {
      if (this._adapter && this._adapter.tag && this._adapter.tag.webkitDroppedFrameCount) {
        return this._adapter.tag.webkitDroppedFrameCount
      }
      return null
    },

    /** Returns content's Duration */
    getDuration: function () {
      var ret = this.options['content.duration']
      if (ret === null && this._adapter) {
        try {
          ret = this._adapter.getDuration()
        } catch (err) {
          Log.warn('An error occured while calling getDuration', err)
        }
      }
      return Util.parseNumber(Math.round(ret), 0)
    },

    /** Returns content's Bitrate */
    getBitrate: function () {
      var ret = this.options['content.bitrate']
      if (ret === null && this._adapter) {
        try {
          ret = this._adapter.getBitrate()
        } catch (err) {
          Log.warn('An error occured while calling getBitrate', err)
        }

        if (!ret || ret === -1) {
          ret = this.getWebkitBitrate()
        }
      }
      return Util.parseNumber(ret, -1)
    },

    /**
     * Returns bitrate as per webkitVideoDecodedByteCount
     *
     * @param {Object} tag Video tag DOM reference.
     * @returns {number}
     */
    getWebkitBitrate: function () {
      if (this._adapter && this._adapter.tag && this._adapter.tag.webkitVideoDecodedByteCount) {
        var bitrate = this._adapter.tag.webkitVideoDecodedByteCount
        if (this._lastWebkitBitrate) {
          var delta = this._adapter.tag.webkitVideoDecodedByteCount - this._lastWebkitBitrate
          bitrate = Math.round(((delta) / this.viewTransform.response.pingTime) * 8)
        }
        this._lastWebkitBitrate = this._adapter.tag.webkitVideoDecodedByteCount
        return bitrate !== 0 ? bitrate : -1
      }
    },

    /** Returns content's Throughput */
    getThroughput: function () {
      var ret = this.options['content.throughput']
      if (ret === null && this._adapter) {
        try {
          ret = this._adapter.getThroughput()
        } catch (err) {
          Log.warn('An error occured while calling getThroughput', err)
        }
      }
      return Util.parseNumber(ret, -1)
    },

    /** Returns content's Rendition */
    getRendition: function () {
      var ret = this.options['content.rendition']
      if (ret === null && this._adapter) {
        try {
          ret = this._adapter.getRendition()
        } catch (err) {
          Log.warn('An error occured while calling getRendition', err)
        }
      }
      return ret
    },

    /** Returns content's Title */
    getTitle: function () {
      var ret = this.options['content.title']
      if (ret === null && this._adapter) {
        try {
          ret = this._adapter.getTitle()
        } catch (err) {
          Log.warn('An error occured while calling getTitle', err)
        }
      }
      return ret
    },

    /** Returns content's Title2 */
    getTitle2: function () {
      var ret = this.options['content.title2']
      if (ret === null && this._adapter) {
        try {
          ret = this._adapter.getTitle2()
        } catch (err) {
          Log.warn('An error occured while calling getTitle2', err)
        }
      }
      return ret
    },

    /** Returns content's IsLive */
    getIsLive: function () {
      var ret = this.options['content.isLive']
      if (ret === null && this._adapter) {
        try {
          ret = this._adapter.getIsLive()
        } catch (err) {
          Log.warn('An error occured while calling getIsLive', err)
        }
      }
      return ret || false
    },

    /** Returns content's Resource after being parsed by the resourceTransform */
    getResource: function () {
      var ret = null
      if (!this.resourceTransform.isBlocking()) {
        ret = this.resourceTransform.getResource()
      }

      return ret || this.getOriginalResource()
    },

    /** Returns content's original Resource */
    getOriginalResource: function () {
      var ret = null
      ret = this.options['content.resource']
      if (ret === null && this._adapter) {
        try {
          ret = this._adapter.getResource()
        } catch (err) {
          Log.warn('An error occured while calling getResource', err)
          ret = null
        }
      }
      return ret || 'unknown'
    },

    /** Returns content's TransactionCode */
    getTransactionCode: function () {
      return this.options['content.transactionCode']
    },

    /** Returns content's Metadata */
    getMetadata: function () {
      return this.options['content.metadata']
    },

    /** Returns content's PlayerVersion */
    getPlayerVersion: function () {
      var ret = ''
      if (this._adapter) {
        try {
          ret = this._adapter.getPlayerVersion()
        } catch (err) {
          Log.warn('An error occured while calling getPlayerVersion', err)
        }
      }
      return ret
    },

    /** Returns content's PlayerName */
    getPlayerName: function () {
      var ret = ''
      if (this._adapter) {
        try {
          ret = this._adapter.getPlayerName()
        } catch (err) {
          Log.warn('An error occured while calling getPlayerName', err)
        }
      }
      return ret
    },

    /** Returns content's Cdn */
    getCdn: function () {
      var ret = null
      if (!this.resourceTransform.isBlocking()) {
        ret = this.resourceTransform.getCdnName()
      }
      return ret || this.options['content.cdn']
    },

    /** Returns content's PluginVersion */
    getPluginVersion: function () {
      var ret = this.getAdapterVersion()
      if (!ret) ret = version + '-adapterless'

      return ret
    },

    /** Returns ads adapter getVersion or null */
    getAdapterVersion: function () {
      var ret = null
      if (this._adapter) {
        try {
          ret = this._adapter.getVersion()
        } catch (err) {
          Log.warn('An error occured while calling getPluginVersion', err)
        }
      }
      return ret
    },

    /** Returns content's Extraparam1 */
    getExtraparam1: function () {
      return this.options['extraparam.1']
    },

    /** Returns content's Extraparam2 */
    getExtraparam2: function () {
      return this.options['extraparam.2']
    },

    /** Returns content's Extraparam3 */
    getExtraparam3: function () {
      return this.options['extraparam.3']
    },

    /** Returns content's Extraparam4 */
    getExtraparam4: function () {
      return this.options['extraparam.4']
    },
    /** Returns content's Extraparam5 */
    getExtraparam5: function () {
      return this.options['extraparam.5']
    },

    /** Returns content's Extraparam6 */
    getExtraparam6: function () {
      return this.options['extraparam.6']
    },

    /** Returns content's Extraparam7 */
    getExtraparam7: function () {
      return this.options['extraparam.7']
    },

    /** Returns content's Extraparam8 */
    getExtraparam8: function () {
      return this.options['extraparam.8']
    },

    /** Returns content's Extraparam9 */
    getExtraparam9: function () {
      return this.options['extraparam.9']
    },

    /** Returns content's Extraparam10 */
    getExtraparam10: function () {
      return this.options['extraparam.10']
    },

    /** Returns ads's PlayerVersion */
    getAdPlayerVersion: function () {
      var ret = ''
      if (this._adsAdapter) {
        try {
          ret = this._adsAdapter.getPlayerVersion()
        } catch (err) {
          Log.warn('An error occured while calling getAdPlayerVersion', err)
        }
      }
      return ret
    },

    /** Returns ad's position */
    getAdPosition: function () {
      var ret = 'pre'
      if (this._adsAdapter) {
        try {
          ret = this._adsAdapter.getPosition()
        } catch (err) {
          Log.warn('An error occured while calling getAdPosition', err)
        }
      }
      if (!ret && this._adapter) {
        ret = (this._adapter.flags.isJoined) ? 'mid' : 'pre'
      }
      return ret
    },

    /** Returns ad's AdPlayhead */
    getAdPlayhead: function () {
      var ret = null
      if (this._adsAdapter) {
        try {
          ret = this._adsAdapter.getPlayhead()
        } catch (err) {
          Log.warn('An error occured while calling getAdPlayhead', err)
        }
      }
      return Util.parseNumber(ret, 0)
    },

    /** Returns ad's AdDuration */
    getAdDuration: function () {
      var ret = null
      if (this._adsAdapter) {
        try {
          ret = this._adsAdapter.getDuration()
        } catch (err) {
          Log.warn('An error occured while calling getAdDuration', err)
        }
      }
      return Util.parseNumber(ret, 0)
    },

    /** Returns ad's AdBitrate */
    getAdBitrate: function () {
      var ret = null
      if (this._adsAdapter) {
        try {
          ret = this._adsAdapter.getBitrate()
        } catch (err) {
          Log.warn('An error occured while calling getAdBitrate', err)
        }

        if (!ret || ret === -1) {
          ret = this.getWebkitAdBitrate()
        }
      }
      return Util.parseNumber(ret, -1)
    },

    /**
     * Returns bitrate as per webkitVideoDecodedByteCount
     *
     * @param {Object} tag Video tag DOM reference.
     * @returns {number}
     */
    getWebkitAdBitrate: function () {
      if (this._adapter && this._adapter.tag && this._adapter.tag.webkitVideoDecodedByteCount) {
        var bitrate = this._adapter.tag.webkitVideoDecodedByteCount
        if (this._lastWebkitAdBitrate) {
          var delta = this._adapter.tag.webkitVideoDecodedByteCount - this._lastWebkitAdBitrate
          bitrate = Math.round(((delta) / this.viewTransform.response.pingTime) * 8)
        }
        this._lastWebkitAdBitrate = this._adapter.tag.webkitVideoDecodedByteCount
        return bitrate !== 0 ? bitrate : -1
      }
    },

    /** Returns ad's AdTitle */
    getAdTitle: function () {
      var ret = null
      if (this._adsAdapter) {
        try {
          ret = this._adsAdapter.getTitle()
        } catch (err) {
          Log.warn('An error occured while calling getAdTitle', err)
        }
      }
      return ret
    },

    /** Returns ad's AdResource */
    getAdResource: function () {
      var ret = null
      if (this._adsAdapter) {
        try {
          ret = this._adsAdapter.getResource()
        } catch (err) {
          Log.warn('An error occured while calling getAdResource', err)
        }
      }
      return ret
    },

    /** Returns ads adapter getVersion or null */
    getAdAdapterVersion: function () {
      var ret = null
      if (this._adsAdapter) {
        try {
          ret = this._adsAdapter.getVersion()
        } catch (err) {
          Log.warn('An error occured while calling getAdsAdapterVersion', err)
        }
      }
      return ret
    },

    /** Returns ad's AdMetadata */
    getAdMetadata: function () {
      return this.options['ad.metadata']
    },

    /** Returns PluginInfo */
    getPluginInfo: function () {
      var ret = {
        lib: version,
        adapter: this.getAdapterVersion(),
        adAdapter: this.getAdAdapterVersion()
      }
      return ret
    },

    /** Returns Ip */
    getIp: function () {
      return this.options['network.ip']
    },

    /** Returns Isp */
    getIsp: function () {
      return this.options['network.isp']
    },

    /** Returns ConnectionType */
    getConnectionType: function () {
      return this.options['network.connectionType']
    },

    /** Returns DeviceCode */
    getDeviceCode: function () {
      return this.options['device.code']
    },

    /** Returns AccountCode */
    getAccountCode: function () {
      return this.options['accountCode']
    },

    /** Returns Username */
    getUsername: function () {
      return this.options['username']
    },

    /** Get URL referer. */
    getReferer: function () {
      var ret = ''
      if (typeof window !== 'undefined' && window.location) {
        ret = window.location.href
      }
      return ret
    },

    /**
     * Returns the nodehost
     */
    getNodeHost: function () {
      return this.resourceTransform.getNodeHost()
    },

    /**
     * Returns the node type id
     */
    getNodeType: function () {
      return this.resourceTransform.getNodeType()
    },

    /**
     * Returns the node type string
     */
    getNodeTypeString: function () {
      return this.resourceTransform.getNodeTypeString()
    },

    // ----------------------------------------- CHRONOS ------------------------------------------

    /** Returns preload chrono delta time */
    getPreloadDuration: function () {
      return this.preloadChrono.getDeltaTime(false)
    },

    getInitDuration: function () {
      return this.initChrono.getDeltaTime(false)
    },

    /** Returns JoinDuration chrono delta time */
    getJoinDuration: function () {
      if (this.isInitiated) return this.getInitDuration()
      return this._adapter ? this._adapter.chronos.join.getDeltaTime(false) : -1
    },

    /** Returns BufferDuration chrono delta time */
    getBufferDuration: function () {
      return this._adapter ? this._adapter.chronos.buffer.getDeltaTime(false) : -1
    },

    /** Returns SeekDuration chrono delta time */
    getSeekDuration: function () {
      return this._adapter ? this._adapter.chronos.seek.getDeltaTime(false) : -1
    },

    /** Returns pauseDuration chrono delta time */
    getPauseDuration: function () {
      return this._adapter ? this._adapter.chronos.pause.getDeltaTime(false) : -1
    },

    /** Returns AdJoinDuration chrono delta time */
    getAdJoinDuration: function () {
      return this._adsAdapter ? this._adsAdapter.chronos.join.getDeltaTime(false) : -1
    },

    /** Returns AdBufferDuration chrono delta time */
    getAdBufferDuration: function () {
      return this._adsAdapter ? this._adsAdapter.chronos.buffer.getDeltaTime(false) : -1
    },

    /** Returns AdPauseDuration chrono delta time */
    getAdPauseDuration: function () {
      return this._adsAdapter ? this._adsAdapter.chronos.pause.getDeltaTime(false) : -1
    },

    /** Returns total totalAdDuration chrono delta time */
    getAdTotalDuration: function () {
      return this._adsAdapter ? this._adsAdapter.chronos.total.getDeltaTime(false) : -1
    }
  },

  /** @lend Plugin */
  {
    // Static Memebers //
    /**
     * List of events that could be fired
     * @enum
     */
    Event: {
      WILL_SEND_START: 'will-send-start',
      WILL_SEND_JOIN: 'will-send-join',
      WILL_SEND_PAUSE: 'will-send-pause',
      WILL_SEND_RESUME: 'will-send-resume',
      WILL_SEND_SEEK: 'will-send-seek',
      WILL_SEND_BUFFER: 'will-send-buffer',
      WILL_SEND_ERROR: 'will-send-error',
      WILL_SEND_FATAL_ERROR: 'will-send-fatal-error',
      WILL_SEND_STOP: 'will-send-stop',
      WILL_SEND_PING: 'will-send-ping',
      WILL_SEND_AD_START: 'will-send-ad-start',
      WILL_SEND_AD_JOIN: 'will-send-ad-join',
      WILL_SEND_AD_PAUSE: 'will-send-ad-pause',
      WILL_SEND_AD_RESUME: 'will-send-ad-resume',
      WILL_SEND_AD_BUFFER: 'will-send-ad-buffer',
      WILL_SEND_AD_STOP: 'will-send-ad-stop'
    }
  }
)

module.exports = Plugin



//////////////////
// WEBPACK FOOTER
// ./src/plugin/plugin.js
// module id = 20
// module chunks = 0